import React, { useState } from 'react';
import Box from '@mui/material/Box';
import StepShell, { PrimaryBtn, SelectionChip } from '../StepShell';
import { QuizAnswer } from '../types';
import { Theme, useMediaQuery, useTheme } from '@mui/material';
import { IngredientToAvoid } from 'src/ts/@types/generated/graphql/catalog-service/graphql';
import _ from 'lodash';
import { IngredientToAvoidIds } from 'src/utils/constants';
import {
  HIDDEN_LINKED_INGREDIENT_IDS,
  LINKED_INGREDIENT_IDS,
} from '../constants';

interface Props {
  state: Partial<QuizAnswer>;
  setState: React.Dispatch<React.SetStateAction<Partial<QuizAnswer>>>;
  onNext: () => void;
  onSkip?: (() => void) | null;
  onBack?: (() => void) | null;
  stepIdx: number;
  total: number;
  menuPool?: string[];
  ingredients: {
    legacyAllergenItemId: number;
    displayName: string;
    isAllergen: boolean;
    category: {
      legacyAllergenCategoryId: number;
      displayName: string;
    };
  }[];
  hideNonAllergens?: boolean;
}

// These were selected by Culinary and Product as ingredients worth highlighting as common allergens: https://coda.io/d/_dd-_fLo_ffA/Main-Allergens_sulfcB7O#Table-5_tudbkNdi
const MAIN_ALLERGEN_IDS = [
  IngredientToAvoidIds.COCONUT,
  IngredientToAvoidIds.DAIRY,
  IngredientToAvoidIds.EGGS,
  IngredientToAvoidIds.FISH,
  IngredientToAvoidIds.PEANUTS,
  IngredientToAvoidIds.SESAME,
  IngredientToAvoidIds.SHELLFISH,
  IngredientToAvoidIds.SOY,
  IngredientToAvoidIds.TREE_NUTS,
  IngredientToAvoidIds.WHEAT,
];

export default function IngredientsToAvoid({
  state,
  setState,
  onNext,
  onSkip,
  onBack,
  stepIdx,
  total,
  ingredients,
  hideNonAllergens,
}: Props) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery<Theme>((t) => t.breakpoints.down('sm'));
  const selectedIngredientIds = state?.ingredientToAvoidIds || [];
  const visibleSelectedCount = selectedIngredientIds.filter(
    (id) => !HIDDEN_LINKED_INGREDIENT_IDS.includes(id),
  ).length;

  const toggle = (ingredientId: number) => {
    setState((prevState) => {
      const selectedIds = prevState?.ingredientToAvoidIds || [];
      const isSelected = selectedIds.includes(ingredientId);
      const linkedIds = LINKED_INGREDIENT_IDS[ingredientId] ?? [];
      const allIds = [ingredientId, ...linkedIds];
      return {
        ...prevState,
        ingredientToAvoidIds: isSelected
          ? selectedIds.filter((id) => !allIds.includes(id))
          : [
              ...selectedIds,
              ...allIds.filter((id) => !selectedIds.includes(id)),
            ],
      };
    });
  };

  const [query, setQuery] = useState('');

  const filteredIngredients = React.useMemo(() => {
    const searchableIngredients = ingredients.filter(
      (ingredient) =>
        !HIDDEN_LINKED_INGREDIENT_IDS.includes(ingredient.legacyAllergenItemId),
    );
    if (!query.trim()) {
      return searchableIngredients;
    }
    return searchableIngredients.filter((ingredient) =>
      ingredient.displayName.toLowerCase().includes(query.toLowerCase()),
    );
  }, [ingredients, query]);

  const groupedIngredients = React.useMemo(() => {
    const groups: Record<string, IngredientToAvoid[]> = {};
    filteredIngredients.forEach((ingredient) => {
      const letter = ingredient.displayName[0].toUpperCase();
      if (!groups[letter]) {
        groups[letter] = [];
      }
      groups[letter].push(ingredient);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredIngredients]);

  const allergens = ingredients.filter((ingredient) =>
    MAIN_ALLERGEN_IDS.includes(ingredient.legacyAllergenItemId),
  );

  return (
    <StepShell
      stepIdx={stepIdx}
      total={total}
      mobile={isSmallScreen}
      onBack={onBack}
      onSkip={onSkip}
      footer={
        <PrimaryBtn onClick={onNext}>
          {visibleSelectedCount === 0
            ? 'Continue'
            : `Continue with ${visibleSelectedCount} selected`}
        </PrimaryBtn>
      }
    >
      <Box sx={{ pt: isSmallScreen ? '4px' : '8px', pb: '16px' }}>
        <Box
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: isSmallScreen ? 30 : 40,
            lineHeight: 1.08,
            letterSpacing: '-0.015em',
            m: 0,
            mb: '6px',
            color: '#041C2C',
            textWrap: 'balance',
          }}
        >
          Anything to avoid?
        </Box>
        <Box
          component="p"
          sx={{
            fontSize: isSmallScreen ? 15 : 17,
            color: theme.palette.text.secondary,
            m: 0,
            mb: isSmallScreen ? '18px' : '24px',
          }}
        >
          Pick anything you'd rather not see. Skip if there's nothing.
        </Box>

        {/* Common allergens card */}
        <Box
          sx={{
            background: '#FFFFFF',
            borderRadius: '16px',
            p: '16px 18px 18px',
            border: '1.5px solid #E5E5EE',
            mb: '22px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: '12px',
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: 'Fraunces, serif',
                fontWeight: 600,
                fontSize: 17,
                letterSpacing: '-0.01em',
                color: '#041C2C',
              }}
            >
              Common allergens
            </Box>
            <Box
              component="span"
              sx={{ fontSize: 12, color: theme.palette.text.secondary }}
            >
              Tap any that apply
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {allergens.map((allergen) => (
              <SelectionChip
                key={allergen.legacyAllergenItemId}
                label={_.capitalize(allergen.displayName)}
                size="lg"
                selected={selectedIngredientIds.includes(
                  allergen.legacyAllergenItemId,
                )}
                onClick={() => toggle(allergen.legacyAllergenItemId)}
              />
            ))}
          </Box>
        </Box>

        {!hideNonAllergens && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                m: '4px 0 14px',
              }}
            >
              <Box
                component="span"
                sx={{ flex: 1, height: 1, background: '#E5E5EE' }}
              />
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                }}
              >
                Or search everything else
              </Box>
              <Box
                component="span"
                sx={{ flex: 1, height: 1, background: '#E5E5EE' }}
              />
            </Box>

            {/* Search input */}
            <Box sx={{ position: 'relative', mb: '14px' }}>
              <Box
                component="input"
                type="text"
                placeholder="Search ingredients to avoid…"
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setQuery(e.target.value)
                }
                aria-label="Search ingredients"
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                  e.target.style.borderColor = theme.palette.primary.main;
                }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  e.target.style.borderColor = '#E5E5EE';
                }}
                sx={{
                  width: '100%',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  fontSize: 15,
                  p: '12px 14px 12px 40px',
                  border: '1.5px solid #E5E5EE',
                  borderRadius: '10px',
                  background: '#FFFFFF',
                  color: '#041C2C',
                  outline: 'none',
                }}
              />
              <Box
                component="svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke={theme.palette.text.secondary}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                sx={{
                  width: '16px',
                  height: '16px',
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </Box>
            </Box>

            {groupedIngredients.length === 0 && (
              <Box
                sx={{
                  p: '24px',
                  textAlign: 'center',
                  color: theme.palette.text.secondary,
                  fontSize: 14,
                }}
              >
                No ingredients match &ldquo;{query}&rdquo;.
              </Box>
            )}

            {groupedIngredients.map(([letter, ingredients]) => (
              <Box key={letter} sx={{ mb: '18px' }}>
                <Box
                  sx={{
                    fontFamily: 'Fraunces, serif',
                    fontWeight: 600,
                    fontSize: 18,
                    letterSpacing: '-0.01em',
                    color: '#041C2C',
                    mb: '10px',
                  }}
                >
                  {letter}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {ingredients.map((ingredient) => (
                    <SelectionChip
                      key={ingredient.legacyAllergenItemId}
                      label={_.capitalize(ingredient.displayName)}
                      selected={selectedIngredientIds.includes(
                        ingredient.legacyAllergenItemId,
                      )}
                      onClick={() => toggle(ingredient.legacyAllergenItemId)}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>
    </StepShell>
  );
}
